import React from 'react';
import { Modal, View, Text, TouchableOpacity, FlatList, StyleSheet } from 'react-native';

import { useAppTheme } from '../../design-system/ThemeProvider';
import { t } from '../../i18n/translations';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../quiz-core/languageStore';

interface Member {
  user_id: string;
  user_profiles?: { name?: string; avatar_url?: string };
  role: string;
}

interface FamilyMembersModalProps {
  visible: boolean;
  onClose: () => void;
  members: Member[];
  currentUserId: string;
  isAdmin: boolean;
  onMakeAdmin?: (userId: string) => void;
}

const FamilyMembersModal: React.FC<FamilyMembersModalProps> = ({
  visible,
  onClose,
  members,
  currentUserId,
  isAdmin,
  onMakeAdmin,
}) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.modalContent, { backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant, borderWidth: 1 }]}>
          <Text style={[styles.title, { color: colors.onSurface }]}>{t('familiesFriends.titleFamilyMembers', language)}</Text>
          <FlatList
            data={members}
            keyExtractor={(item) => item.user_id}
            renderItem={({ item }) => (
              <View style={[styles.memberRow, { borderBottomColor: colors.outlineVariant }]}>
                <Text style={[styles.memberName, { color: colors.onSurface }]}>{item.user_profiles?.name || 'Unknown'}</Text>
                <Text style={[styles.memberRole, { color: colors.onSurfaceVariant }]}>
                  {item.role === 'owner' ? t('familiesFriends.roleOwner', language) : item.role === 'admin' ? t('familiesFriends.roleAdmin', language) : t('familiesFriends.roleMember', language)}
                </Text>
                {isAdmin && item.role !== 'owner' && item.user_id !== currentUserId && (
                  <TouchableOpacity
                    style={[styles.makeAdminBtn, { backgroundColor: colors.primary }]}
                    onPress={() => onMakeAdmin && onMakeAdmin(item.user_id)}
                  >
                    <Text style={styles.makeAdminText}>{t('familiesFriends.buttonMakeAdmin', language)}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          />
          <PrimaryButton title={t('familiesFriends.buttonClose', language)} onPress={onClose} style={{ marginTop: 18 }} />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    borderRadius: 12,
    padding: 24,
    minWidth: 300,
    maxWidth: 340,
    width: '90%',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    width: '100%',
    borderBottomWidth: 1,
    paddingBottom: 10,
  },
  memberName: {
    fontSize: 16,
    flex: 1,
  },
  memberRole: {
    fontSize: 15,
    marginLeft: 10,
    marginRight: 10,
    minWidth: 70,
    textAlign: 'right',
  },
  makeAdminBtn: {
    backgroundColor: '#3381c6',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  makeAdminText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 13,
  },
});

export default FamilyMembersModal;
